import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
} from "lucide-react";
import DashboardMock from "./DashboardMock";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 cb-grid-bg [mask-image:radial-gradient(ellipse_at_top,black_25%,transparent_75%)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-[#3B82F6]/8 blur-[120px] rounded-full opacity-50" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.28fr)] gap-12 lg:gap-14 items-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.08 } },
            }}
          >
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5 shadow-sm"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6] cb-pulse" />
              <span className="font-inter text-[11px] font-bold tracking-[0.22em] uppercase text-[#0B1F3A]">
                Workspace Product
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="hero-headline"
              className="mt-6 font-display text-[44px] sm:text-5xl lg:text-[64px] font-extrabold tracking-[-0.025em] text-[#0B1F3A] leading-[1.04]"
            >
              CommodityBid<span className="text-[#3B82F6]">™️</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="hero-subheadline"
              className="mt-5 font-display text-xl sm:text-2xl lg:text-[26px] font-semibold tracking-tight text-[#0B1F3A] leading-snug"
            >
              One Bidding Request.
              <br className="hidden sm:block" />
              <span className="text-[#475569] font-semibold">Multiple Verified Manufacturers. One Winning Decision.</span>
            </motion.p>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="hero-description"
              className="mt-6 max-w-xl text-[15px] md:text-base text-[#475569] leading-relaxed"
            >
              Launch a procurement bidding request once.
              <br />
              <span className="text-[#0B1F3A] font-medium">Verified Turkish manufacturers participate online and submit competitive factory offers.</span>{" "}
              Review every offer inside one Workspace and award the manufacturer that best fits your business.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link
                to="/workspace"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 bg-[#0B1F3A] hover:bg-[#0B1F3A]/92 text-white text-[15px] font-medium px-6 py-3.5 rounded-xl shadow-sm transition-all hover:shadow-lg hover:translate-y-[-1px]"
              >
                Launch CommodityBid™️
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/workspace"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-[#0B1F3A] border border-slate-200 text-[15px] font-medium px-6 py-3.5 rounded-xl shadow-sm transition-all"
              >
                Create Your Free Workspace
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1], delay: 0.15 }}
            className="relative"
          >
            <DashboardMock />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
